import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle, ZoomIn, Pencil, Save, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const DOT_COUNTIES = ['collin', 'coryell', 'brewster', 'whatcom', 'comal', 'dallas', 'gillespie', 'sherman', 'fannin', 'goliad', 'sanjuan'];
const TEXAS_COUNTIES = [
  'bandera', 'bell', 'blanco', 'brazos', 'brewster', 'collin', 'comal', 'coryell',
  'dallas', 'gillespie', 'goliad', 'guadalupe', 'hidalgo', 'hockley', 'jimhogg',
  'kendall', 'kerr', 'kleberg', 'midland', 'nueces', 'potter', 'randall', 'reagan',
  'reeves', 'sanpatricio', 'travis', 'williamson', 'zapata'
];
const isDotCounty = (c: string) => DOT_COUNTIES.includes((c || '').toLowerCase());
const isTexasCounty = (c: string) => TEXAS_COUNTIES.includes((c || '').toLowerCase());

interface DirectScreenshotCaptureProps {
  county: string;
  onRecordSaved?: () => void;
}

export function DirectScreenshotCapture({ county, onRecordSaved }: DirectScreenshotCaptureProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [savedRecordId, setSavedRecordId] = useState<string | null>(null);
  
  // Zoom-for-address state
  const [zoomMode, setZoomMode] = useState(false);
  const [zoomImageData, setZoomImageData] = useState<string | null>(null);
  const [isZoomProcessing, setIsZoomProcessing] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Melissa verification state
  const [isVerifying, setIsVerifying] = useState(false);
  const [melissaResult, setMelissaResult] = useState<{ status: string; statusReason: string; md_address?: string; md_city?: string; md_state?: string; md_zip?: string } | null>(null);

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            if (zoomMode) {
              setZoomImageData(base64);
            } else {
              setImageData(base64);
            }
          };
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleClipboardButton = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(t => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            if (zoomMode) {
              setZoomImageData(base64);
            } else {
              setImageData(base64);
            }
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      toast.error("No image found in clipboard");
    } catch {
      toast.error("Clipboard access denied — use Ctrl+V instead");
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !isProcessing && !isZoomProcessing) {
        if (zoomMode && zoomImageData) {
          e.preventDefault();
          document.getElementById("zoom-extract-btn")?.click();
        } else if (!zoomMode && imageData) {
          e.preventDefault();
          document.getElementById("extract-save-btn")?.click();
        }
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [imageData, isProcessing, zoomMode, zoomImageData, isZoomProcessing]);

  const handleExtractAndSave = async () => {
    if (!imageData) {
      toast.error("Please paste a screenshot first");
      return;
    }

    setIsProcessing(true);
    setExtractedData(null);
    setSavedRecordId(null);

    try {
      const { data, error } = await supabase.functions.invoke("extract-contract-screenshot", {
        body: {
          imageData,
          county,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Contract data extracted and saved!");
        setExtractedData(data.extractedData);
        setSavedRecordId(data.record?.id || null);
        onRecordSaved?.();
        // Don't auto-clear — let user choose to zoom for address
      } else {
        throw new Error(data.error || "Failed to extract data");
      }
    } catch (error: any) {
      console.error("Extract error:", error);
      toast.error(error.message || "Failed to extract contract data");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleZoomExtract = async () => {
    if (!zoomImageData || !savedRecordId) {
      toast.error("Missing zoomed screenshot or record ID");
      return;
    }

    setIsZoomProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke("extract-contract-screenshot", {
        body: {
          imageData: zoomImageData,
          county,
          recordId: savedRecordId,
          addressOnly: true,
        },
      });

      if (error) throw error;

      if (data.success) {
        const isDot = isDotCounty(county);
        // Zoom extraction always returns grantor_address, but for DOT counties it should map to grantee_address
        const addr = data.extractedData?.grantor_address;
        const addrField = isDot ? 'grantee_address' : 'grantor_address';
        toast.success(addr ? `${isDot ? 'Lender' : 'Seller'} address updated: ${addr}` : "Address extraction complete");
        setExtractedData((prev: any) => ({
          ...prev,
          [addrField]: addr || prev?.[addrField],
        }));
        onRecordSaved?.();

        // Now clear everything for next record
        setTimeout(() => {
          resetAll();
        }, 3000);
      } else {
        throw new Error(data.error || "Failed to extract address");
      }
    } catch (error: any) {
      console.error("Zoom extract error:", error);
      toast.error(error.message || "Failed to extract address from zoomed screenshot");
    } finally {
      setIsZoomProcessing(false);
    }
  };

  const resetAll = () => {
    setImageData(null);
    setExtractedData(null);
    setSavedRecordId(null);
    setZoomMode(false);
    setZoomImageData(null);
    setIsEditing(false);
    setMelissaResult(null);
  };

  const handleVerifyWithMelissa = async () => {
    const isDot = isDotCounty(county);
    const sellerAddr = isDot ? extractedData?.grantee_address : extractedData?.grantor_address;
    const sellerName = isDot ? extractedData?.grantee_name : extractedData?.grantor_name;
    if (!sellerAddr) {
      toast.error("No seller address to verify");
      return;
    }

    setIsVerifying(true);
    setMelissaResult(null);

    try {
      // Parse the address into components (best effort)
      const addr = sellerAddr;
      const parts = addr.split(",").map((p: string) => p.trim());
      
      let street = parts[0] || addr;
      let city = parts[1] || "";
      let stateZip = parts[2] || "";
      let state = "";
      let zip = "";
      
      if (stateZip) {
        const szParts = stateZip.trim().split(/\s+/);
        state = szParts[0] || "";
        zip = szParts.slice(1).join(" ") || "";
      }

      const record = {
        seller_id: savedRecordId || "verify-check",
        street_address: street,
        city,
        state,
        zip_code: zip,
        first_name: (sellerName || '').split(" ")[0] || "",
        last_name: (sellerName || '').split(" ").slice(1).join(" ") || "",
      };

      const { data, error } = await supabase.functions.invoke("melissa-address-verify", {
        body: { records: [record] },
      });

      if (error) throw error;

      if (data?.success && data.records?.length > 0) {
        const result = data.records[0];
        setMelissaResult({
          status: result.status,
          statusReason: result.statusReason,
          md_address: result.md_address,
          md_city: result.md_city,
          md_state: result.md_state,
          md_zip: result.md_zip,
        });

        if (result.status === "good") {
          toast.success("✅ Address verified — it's a real, deliverable address!");
        } else if (result.status === "review") {
          toast.warning("⚠️ Address needs review: " + result.statusReason);
        } else {
          toast.error("❌ Address not valid: " + result.statusReason);
        }
      } else {
        throw new Error(data?.error || "Melissa verification failed");
      }
    } catch (err: any) {
      console.error("Melissa verify error:", err);
      toast.error(err.message || "Failed to verify address");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!savedRecordId || !extractedData) return;
    setIsSavingEdit(true);
    try {
      const isDot = isDotCounty(county);
      // For DOT counties: the "seller" (lender) address goes to grantee_address
      // For land contract counties: seller address goes to grantor_address
      const addressUpdate = isDot
        ? { grantee_address: extractedData.grantee_address || null, grantor_address: extractedData.grantor_address || null }
        : { grantor_address: extractedData.grantor_address || null, grantee_address: extractedData.grantee_address || null };

      const { error } = await supabase
        .from("county_research")
        .update({
          document_number: extractedData.document_number,
          filing_date: extractedData.filing_date,
          grantor_name: extractedData.grantor_name,
          grantee_name: extractedData.grantee_name,
          property_address: extractedData.property_address || null,
          ...addressUpdate,
          loan_amount: extractedData.loan_amount || null,
          interest_rate: extractedData.interest_rate || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", savedRecordId);
      if (error) throw error;
      toast.success("Record updated successfully");
      setIsEditing(false);
      onRecordSaved?.();
    } catch (err: any) {
      toast.error(err.message || "Failed to save edits");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const startZoomMode = () => {
    setZoomMode(true);
    setZoomImageData(null);
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Direct Screenshot Research Bot
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Paste a screenshot of a land contract document and it will automatically extract all fields and save to the database.
            </p>
          </div>
          {imageData && !zoomMode && (
            <div className="flex gap-2 shrink-0 ml-4">
              <Button
                onClick={handleExtractAndSave}
                disabled={isProcessing}
                size="sm"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Extract & Save
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImageData(null)}
                disabled={isProcessing}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Step 1: Main screenshot capture */}
        {!zoomMode && (
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
            onPaste={handlePaste}
            tabIndex={0}
          >
            {!imageData ? (
              <div className="space-y-2">
                <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-muted-foreground mb-3">
                  Click here and press <kbd className="px-2 py-1 bg-muted rounded">Ctrl+V</kbd> to paste, or:
                </p>
                <Button variant="outline" size="sm" onClick={handleClipboardButton}>
                  📋 Paste from Clipboard
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <img
                  src={imageData}
                  alt="Pasted screenshot"
                  className="max-h-96 mx-auto rounded border"
                />
                <div className="flex gap-2 justify-center">
                  <Button
                    id="extract-save-btn"
                    onClick={handleExtractAndSave}
                    disabled={isProcessing}
                    size="lg"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Extracting Data...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Extract & Save (Enter)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setImageData(null)}
                    disabled={isProcessing}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Zoom for address */}
        {zoomMode && (
          <div
            className="border-2 border-dashed border-primary/50 rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none bg-primary/5"
            onPaste={handlePaste}
            tabIndex={0}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <ZoomIn className="h-5 w-5 text-primary" />
              <span className="font-semibold text-primary">Zoom Screenshot for Address</span>
            </div>
            {!zoomImageData ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Zoom into the <strong>seller's address</strong> in the document, take a screenshot, and paste it here.
                </p>
                <p className="text-xs text-muted-foreground">
                  Look for the address right before "referred to as Seller" in the opening paragraph.
                </p>
                <div className="flex gap-2 justify-center mt-3">
                  <Button variant="outline" size="sm" onClick={handleClipboardButton}>
                    📋 Paste Zoomed Screenshot
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setZoomMode(false); resetAll(); }}>
                    Skip & Next Record
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <img
                  src={zoomImageData}
                  alt="Zoomed screenshot"
                  className="max-h-96 mx-auto rounded border"
                />
                <div className="flex gap-2 justify-center">
                  <Button
                    id="zoom-extract-btn"
                    onClick={handleZoomExtract}
                    disabled={isZoomProcessing}
                    size="lg"
                    className="bg-primary"
                  >
                    {isZoomProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Extracting Address...
                      </>
                    ) : (
                      <>
                        <ZoomIn className="mr-2 h-4 w-4" />
                        Extract Address (Enter)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setZoomImageData(null)}
                    disabled={isZoomProcessing}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Extraction results */}
        {extractedData && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-green-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Extracted & Saved
              </h4>
              {!isEditing ? (
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                  <Pencil className="mr-1 h-3 w-3" /> Edit
                </Button>
              ) : (
                <Button variant="default" size="sm" onClick={handleSaveEdit} disabled={isSavingEdit}>
                  {isSavingEdit ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
                  Save Changes
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {(() => {
                const isDot = isDotCounty(county);
                const isTexas = isTexasCounty(county);
                const lenderLabel = isTexas ? 'Lender' : 'Beneficiary/Lender';
                const borrowerLabel = isTexas ? 'Borrower' : 'Buyer';
                const sellerLabel = isDot ? lenderLabel : 'Seller (Grantor)';
                const buyerLabel = isDot ? borrowerLabel : 'Buyer (Grantee)';
                // For DOT: lender address = grantee_address; borrower address = grantor_address
                const sellerAddrField = isDot ? 'grantee_address' : 'grantor_address';
                const borrowerAddrField = isDot ? 'grantor_address' : 'grantee_address';
                const sellerAddr = extractedData[sellerAddrField] || '';
                const borrowerAddr = extractedData[borrowerAddrField] || '';

                if (isEditing) {
                  return (
                    <>
                      <div><label className="font-medium text-xs">Document #</label><Input value={extractedData.document_number || ""} onChange={e => setExtractedData((p: any) => ({ ...p, document_number: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">Filing Date</label><Input value={extractedData.filing_date || ""} onChange={e => setExtractedData((p: any) => ({ ...p, filing_date: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">{isDot ? `${lenderLabel} (Grantee)` : 'Seller (Grantor)'}</label><Input value={isDot ? (extractedData.grantee_name || "") : (extractedData.grantor_name || "")} onChange={e => setExtractedData((p: any) => ({ ...p, [isDot ? 'grantee_name' : 'grantor_name']: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">{isDot ? `${borrowerLabel} (Grantor)` : 'Buyer (Grantee)'}</label><Input value={isDot ? (extractedData.grantor_name || "") : (extractedData.grantee_name || "")} onChange={e => setExtractedData((p: any) => ({ ...p, [isDot ? 'grantor_name' : 'grantee_name']: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">Property Address</label><Input value={extractedData.property_address || ""} onChange={e => setExtractedData((p: any) => ({ ...p, property_address: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs text-primary">{sellerLabel} Mailing Address</label><Input value={sellerAddr} onChange={e => setExtractedData((p: any) => ({ ...p, [sellerAddrField]: e.target.value }))} className="h-7 text-sm border-primary/50" /></div>
                      <div><label className="font-medium text-xs">{buyerLabel} Address</label><Input value={borrowerAddr} onChange={e => setExtractedData((p: any) => ({ ...p, [borrowerAddrField]: e.target.value }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">Loan Amount</label><Input type="number" value={extractedData.loan_amount || ""} onChange={e => setExtractedData((p: any) => ({ ...p, loan_amount: e.target.value ? Number(e.target.value) : null }))} className="h-7 text-sm" /></div>
                      <div><label className="font-medium text-xs">Interest Rate (%)</label><Input type="number" step="0.01" value={extractedData.interest_rate || ""} onChange={e => setExtractedData((p: any) => ({ ...p, interest_rate: e.target.value ? Number(e.target.value) : null }))} className="h-7 text-sm" /></div>
                    </>
                  );
                }
                return (
                  <>
                    <div><span className="font-medium">Document #:</span> {extractedData.document_number}</div>
                    <div><span className="font-medium">Filing Date:</span> {extractedData.filing_date}</div>
                    <div><span className="font-medium">{sellerLabel}:</span> {isDot ? extractedData.grantee_name : extractedData.grantor_name}</div>
                    <div><span className="font-medium">{buyerLabel}:</span> {isDot ? extractedData.grantor_name : extractedData.grantee_name}</div>
                    <div><span className="font-medium">Property:</span> {extractedData.property_address}</div>
                    <div><span className="font-medium">{sellerLabel} Address:</span> {sellerAddr || <span className="text-amber-600 italic">Not extracted</span>}</div>
                    <div><span className="font-medium">{buyerLabel} Address:</span> {borrowerAddr || <span className="text-muted-foreground italic">N/A</span>}</div>
                    <div><span className="font-medium">Loan Amount:</span> {extractedData.loan_amount ? `$${extractedData.loan_amount.toLocaleString()}` : 'N/A'}</div>
                    <div><span className="font-medium">Interest Rate:</span> {extractedData.interest_rate ? `${extractedData.interest_rate}%` : 'N/A'}</div>
                  </>
                );
              })()}
            </div>

            {/* Melissa verification result */}
            {melissaResult && (
              <div className={`mt-3 p-3 rounded-lg border text-sm ${
                melissaResult.status === 'good' ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' :
                melissaResult.status === 'review' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800' :
                'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={melissaResult.status === 'good' ? 'default' : melissaResult.status === 'review' ? 'secondary' : 'destructive'}>
                    {melissaResult.status === 'good' ? '✅ Verified' : melissaResult.status === 'review' ? '⚠️ Review' : '❌ Invalid'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{melissaResult.statusReason}</span>
                </div>
                {melissaResult.md_address && (
                  <div className="text-xs mt-1">
                    <span className="font-medium">Melissa standardized:</span>{" "}
                    {melissaResult.md_address}, {melissaResult.md_city}, {melissaResult.md_state} {melissaResult.md_zip}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            {savedRecordId && !zoomMode && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {(isDotCounty(county) ? extractedData.grantee_address : extractedData.grantor_address) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleVerifyWithMelissa}
                    disabled={isVerifying}
                    className="border-green-500/50 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30"
                  >
                    {isVerifying ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                    ) : (
                      <><ShieldCheck className="mr-2 h-4 w-4" /> Verify with Melissa</>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startZoomMode}
                  className="border-primary/50 text-primary hover:bg-primary/10"
                >
                  <ZoomIn className="mr-2 h-4 w-4" />
                  Zoom Screenshot for Address
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetAll}
                >
                  Next Record →
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="text-xs text-muted-foreground">
        <p><strong>Instructions:</strong></p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Take a full screenshot of the land contract document (Win+Shift+S or Cmd+Shift+4)</li>
          <li>Click in the paste area above and press Ctrl+V (or Cmd+V) to paste</li>
          <li>Click "Extract & Save" to extract all fields and save to database</li>
          <li><strong>Optional:</strong> If the seller address wasn't readable, click "Zoom Screenshot for Address" — zoom into the address on the document, screenshot it, and paste to re-extract just the address</li>
        </ol>
      </div>
    </div>
  );
}
